import { useState, type FormEvent } from 'react';
import { useSettings, useUpdateSettings, useLogout, useExportUrl } from '@/hooks/api';
import { Badge, ErrorState, PageHeader, Spinner } from '@/components/ui';
import { ChangePasswordForm } from '@/components/ChangePasswordForm';

interface ReminderField {
  key: string;
  label: string;
  enabledKey: string;
}

const REMINDER_FIELDS: ReminderField[] = [
  { key: 'dailyBriefTime', label: 'Daily brief time', enabledKey: 'dailyBriefEnabled' },
  { key: 'clockoutTime', label: 'Clock-out time', enabledKey: 'clockoutEnabled' },
  { key: 'endOfDayTime', label: 'End-of-day summary time', enabledKey: 'endOfDayEnabled' },
  { key: 'weeklyReviewTime', label: 'Weekly review time', enabledKey: 'weeklyReviewEnabled' },
];

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function SettingsPage() {
  const { data, isLoading, isError, error, refetch } = useSettings();
  const update = useUpdateSettings();
  const logout = useLogout();
  const [saved, setSaved] = useState(false);

  const jsonUrl = useExportUrl('json');
  const csvGoalsUrl = useExportUrl('csv', 'goals');
  const csvActivitiesUrl = useExportUrl('csv', 'activities');
  const csvNotesUrl = useExportUrl('csv', 'notes');

  const [form, setForm] = useState<Record<string, string | boolean | number>>({});

  const value = (key: string): string | boolean | number => {
    if (key in form) return form[key] as never;
    const settings = data?.settings as Record<string, unknown> | undefined;
    return (settings?.[key] ?? '') as string | boolean | number;
  };

  const setValue = (key: string, v: string | boolean | number) => {
    setForm((f) => ({ ...f, [key]: v }));
    setSaved(false);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!data) return;
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(form)) {
      patch[k] = v;
    }
    if (Object.keys(patch).length === 0) return;
    update.mutate(patch, { onSuccess: () => setSaved(true) });
  };

  if (isLoading) return <Spinner label="Loading settings…" />;
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Failed to load settings'}
        onRetry={() => void refetch()}
      />
    );
  }
  if (!data) return null;

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Settings" subtitle="Times, reminders, and data" />

      <form onSubmit={submit} className="space-y-6">
        <section className="card p-4" aria-labelledby="profile-heading">
          <h2 id="profile-heading" className="mb-3 text-sm font-semibold text-muted">
            Profile
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="label">Display name</p>
              <p className="text-sm">{data.user?.displayName ?? '—'}</p>
            </div>
            <div>
              <label htmlFor="tz" className="label">
                Timezone
              </label>
              <input
                id="tz"
                className="input"
                value={String(value('timezone'))}
                onChange={(e) => setValue('timezone', e.target.value)}
                placeholder="Asia/Jakarta"
              />
            </div>
          </div>
        </section>

        <section className="card p-4" aria-labelledby="reminders-heading">
          <h2 id="reminders-heading" className="mb-3 text-sm font-semibold text-muted">
            Reminders (local time)
          </h2>
          <div className="space-y-4">
            {REMINDER_FIELDS.map((field) => (
              <div key={field.key} className="flex items-center gap-3">
                <label className="flex min-w-0 flex-1 items-center gap-3">
                  <input
                    type="checkbox"
                    checked={Boolean(value(field.enabledKey))}
                    onChange={(e) => setValue(field.enabledKey, e.target.checked)}
                    aria-label={`${field.label} enabled`}
                  />
                  <span className="truncate text-sm">{field.label}</span>
                </label>
                <input
                  type="time"
                  className="input w-32"
                  value={String(value(field.key))}
                  onChange={(e) => setValue(field.key, e.target.value)}
                  aria-label={field.label}
                />
              </div>
            ))}
            <div>
              <label htmlFor="weekly-day" className="label">
                Weekly review day
              </label>
              <select
                id="weekly-day"
                className="input w-auto"
                value={String(value('weeklyReviewDay'))}
                onChange={(e) => setValue('weeklyReviewDay', Number(e.target.value))}
              >
                {WEEKDAYS.map((d, i) => (
                  <option key={d} value={i}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="card p-4" aria-labelledby="discord-heading">
          <h2 id="discord-heading" className="mb-3 text-sm font-semibold text-muted">
            Discord connection
          </h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-2">
              <dt className="text-muted">Discord user ID</dt>
              <dd className="truncate font-mono">{data.user?.discordUserId ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted">Status</dt>
              <dd>
                <Badge tone="success">connected</Badge>
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-muted">
            Use slash commands in Discord (/goal, /note, /activity, /done, /today, /greeting,
            /clockout) to capture anything from chat — it syncs here automatically.
          </p>
        </section>

        <section className="card p-4" aria-labelledby="greeting-heading">
          <h2 id="greeting-heading" className="mb-1 text-sm font-semibold text-muted">
            Greeting &amp; weather (/greeting)
          </h2>
          <p className="mb-3 text-xs text-muted">
            Weekday location is used Mon–Fri, weekend location Sat–Sun. Weather comes from
            Open-Meteo (free) and is cached per day. The schedule is evaluated in Asia/Jakarta
            (WIB): Tue–Sun at the regular time, Monday at the Monday time.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="greeting-weekday-location" className="label">
                Weekday location
              </label>
              <input
                id="greeting-weekday-location"
                className="input"
                value={String(value('greetingWeekdayLocation'))}
                onChange={(e) => setValue('greetingWeekdayLocation', e.target.value)}
                placeholder="Pacitan"
                maxLength={100}
              />
            </div>
            <div>
              <label htmlFor="greeting-weekend-location" className="label">
                Weekend location
              </label>
              <input
                id="greeting-weekend-location"
                className="input"
                value={String(value('greetingWeekendLocation'))}
                onChange={(e) => setValue('greetingWeekendLocation', e.target.value)}
                placeholder="Madiun"
                maxLength={100}
              />
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={Boolean(value('greetingScheduleEnabled'))}
                onChange={(e) => setValue('greetingScheduleEnabled', e.target.checked)}
                aria-label="Scheduled greeting enabled"
              />
              <span>Send /greeting automatically</span>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="greeting-time" className="label">
                  Regular greeting time (Tue–Sun)
                </label>
                <input
                  id="greeting-time"
                  type="time"
                  className="input w-32"
                  value={String(value('greetingTime'))}
                  onChange={(e) => setValue('greetingTime', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="greeting-monday-time" className="label">
                  Monday greeting time
                </label>
                <input
                  id="greeting-monday-time"
                  type="time"
                  className="input w-32"
                  value={String(value('greetingMondayTime'))}
                  onChange={(e) => setValue('greetingMondayTime', e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>
      </form>

      {/* Own form element: nested <form> is invalid HTML and breaks submits. */}
      <div className="mt-6">
        <ChangePasswordForm />
      </div>

      <form onSubmit={submit} className="mt-6 space-y-6">
        <section className="card p-4" aria-labelledby="export-heading">
          <h2 id="export-heading" className="mb-3 text-sm font-semibold text-muted">
            Export data
          </h2>
          <div className="flex flex-wrap gap-2">
            <a className="btn-ghost" href={jsonUrl} download>
              Download JSON
            </a>
            <a className="btn-ghost" href={csvGoalsUrl} download>
              Goals CSV
            </a>
            <a className="btn-ghost" href={csvActivitiesUrl} download>
              Activities CSV
            </a>
            <a className="btn-ghost" href={csvNotesUrl} download>
              Notes CSV
            </a>
          </div>
        </section>

        {saved && (
          <p role="status" className="text-sm text-success">
            Settings saved.
          </p>
        )}
        {update.isError && (
          <p role="alert" className="text-sm text-danger">
            {update.error instanceof Error ? update.error.message : 'Failed to save settings'}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <button type="submit" className="btn-primary" disabled={update.isPending || Object.keys(form).length === 0}>
            {update.isPending ? <Spinner label="Saving…" /> : 'Save settings'}
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            Log out
          </button>
        </div>
      </form>
    </div>
  );
}
